import type { TournamentDraft } from '~/features/schedule/schedule-store';
import { ScoringMode, type TournamentFormat } from '~/shared/api/generated';
import { queryKeys } from '~/shared/api/query-keys';
import { type PlanTournamentBody, tournamentApi } from '~/shared/api/tournament-api';

export interface PlanParams {
  format: TournamentFormat;
  scoring: ScoringMode;
  pointsTarget: number | null;
  participants: number | null;
}

export const planBodyOf = (p: PlanParams): PlanTournamentBody | null => {
  if (p.participants == null) return null;
  if (p.scoring !== ScoringMode.TENNIS_SETS) {
    if (p.pointsTarget == null) return null;
    return { format: p.format, scoring: p.scoring, pointsTarget: p.pointsTarget, participants: p.participants };
  }
  return { format: p.format, scoring: p.scoring, participants: p.participants };
};

const PLAN_STALE_MS = 30_000;

export const planFetchOptions = (body: PlanTournamentBody) => ({
  queryKey: queryKeys.tournament.plan(body),
  queryFn: ({ signal }: { signal: AbortSignal }) => tournamentApi.plan(body, { signal }),
  staleTime: PLAN_STALE_MS,
  retry: false,
});

export const tournamentPlanQuery = (body: PlanTournamentBody | null, enabled = true) => ({
  queryKey: queryKeys.tournament.plan(body),
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    body ? tournamentApi.plan(body, { signal }) : Promise.reject(new Error('Параметры турнира не заданы')),
  enabled: enabled && body !== null,
  staleTime: PLAN_STALE_MS,
  retry: false,
});

export const planBodyOfDraft = (d: TournamentDraft): PlanTournamentBody | null =>
  planBodyOf({
    format: d.format,
    scoring: d.scoring,
    pointsTarget: d.pointsPerMatch,
    participants: d.expectedParticipants,
  });

export const durationText = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} мин`;
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
};

export const courtPickRefusal = (selected: readonly string[], id: string, needed: number | null): string | null => {
  if (selected.includes(id)) return null;
  if (needed === null) return 'План турнира ещё не посчитан — корты выбираются по нему';
  if (selected.length >= needed)
    return `По плану турнира нужно ровно ${courtsText(needed)} — снимите выбранный, чтобы взять другой`;
  return null;
};

export const courtsText = (n: number): string => {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return `${n} кортов`;
  if (last === 1) return `${n} корт`;
  if (last >= 2 && last <= 4) return `${n} корта`;
  return `${n} кортов`;
};
